import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, CheckCircle, Phone, Clock, Shield, ArrowRight, Building, Home, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";
import regionsData, { isNetwork } from "./serviceAreas";
import { localBusiness, businessRef } from "@/lib/business";

export default function ServiceArea() {
  const { regionId } = useParams();

  const currentRegion = regionsData[regionId] || regionsData["darwin"];
  // A "network" area is one we do NOT inspect ourselves: we capture the
  // enquiry and hand it to a local inspector. Everything user-facing and
  // everything in the schema has to say so. See serviceAreas.js.
  const network = isNetwork(currentRegion);
  const operator = currentRegion.operator || null;

  const locationSchema = network
    ? {
        // We provide a MATCHING service here, not an inspection. No
        // LocalBusiness with a Darwin address pretending to trade in Sydney,
        // and no opening hours or ratings we cannot stand behind locally.
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Building and pest inspection referral",
        "name": `Find a building inspector in ${currentRegion.name}`,
        "description": currentRegion.description,
        "provider": businessRef,
        "areaServed": {
          "@type": "City",
          "name": currentRegion.name,
          "containedInPlace": { "@type": "State", "name": currentRegion.stateName },
        },
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "AUD",
          "description":
            "Free to enquire. We pass your details to a local inspector who quotes you directly.",
        },
      }
    : {
        // Our own coverage: the same single business, serving another area.
        // `areaServed` is what says "we work here" — `geo` says "we ARE here",
        // so it stays the office. Four pages each claiming a different
        // location for one business is the entity confusion we are fixing.
        "@context": "https://schema.org",
        ...localBusiness({
          description: currentRegion.description,
          areaServed: {
            "@type": "AdministrativeArea",
            "name": currentRegion.name,
            "containedInPlace": { "@type": "State", "name": currentRegion.stateName },
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            "name": "Building Inspection Services",
            "itemListElement": [
              { "@type": "Service", "name": "Pre-Sale Property Inspections", "description": `Pre-sale inspections in ${currentRegion.name}` },
              { "@type": "Service", "name": "Building Condition Reports", "description": `Building condition assessments in ${currentRegion.name}` },
              { "@type": "Service", "name": "Pool Reports", "description": `Pool safety inspections in ${currentRegion.name}` },
            ],
          },
        }),
      };

  const services = [
    { name: "Pre-Sale Inspections", path: "/services/pre-sale", icon: Building },
    { name: "Building Condition Reports", path: "/services/building-condition", icon: Home },
    { name: "Building Status Reports", path: "/services/building-status", icon: Shield },
    { name: "Pool Reports", path: "/services/pool", icon: CheckCircle }
  ];

  return (
    <div>
      <SEO
        title={currentRegion.title}
        titleSuffix={network ? "Aussie Inspections" : undefined}
        description={network
          ? `Need a building or pest inspection in ${currentRegion.name}? Tell us about the property and we will pass your enquiry to a local inspector, free. Covering ${currentRegion.suburbs.slice(0, 5).join(", ")} and surrounding suburbs.`
          : `Professional building and property inspections in ${currentRegion.name}. Serving ${currentRegion.suburbs.slice(0, 5).join(", ")} and surrounding areas. From $260 incl. GST with 72-hour report delivery.`}
        keywords={`building inspection ${currentRegion.name}, property inspection ${currentRegion.name}, pre-purchase inspection ${currentRegion.name}, ${currentRegion.suburbs.slice(0, 10).join(", ")}`}
        url={`/service-areas/${regionId}`}
        schema={locationSchema}
      />

      {/* CTA Banner */}
      <CTABanner
        heading={network ? `Need an inspector in ${currentRegion.name}?` : undefined}
        subline={
          network
            ? "Free to enquire • We pass it to a local inspector • They quote you direct"
            : undefined
        }
      />

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center bg-slate-900">
        <div className="absolute inset-0">
          <img
            src={currentRegion.heroImage}
            alt={`Building inspections in ${currentRegion.name}`}
            className="w-full h-full object-cover opacity-30"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center space-x-2 text-secondary mb-4">
              <MapPin className="w-5 h-5" />
              <span className="font-semibold uppercase tracking-wider text-sm">
                {network ? `${currentRegion.state} \u2014 Inspector Network` : "Service Area"}
              </span>
            </div>
            <h1 className="font-chivo text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none text-white mb-4">
              {currentRegion.title}
            </h1>
            <p className="text-lg text-slate-200">{currentRegion.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar: our own track record, so only on areas we inspect
          ourselves. Showing "500+ inspections" on a city we have never
          worked in would be a straightforward misrepresentation. */}
      {!network && (
      <section className="bg-primary py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center text-white">
            <div>
              <div className="text-3xl font-bold">{currentRegion.stats.inspections}</div>
              <div className="text-blue-100 text-sm">Inspections Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold">{currentRegion.stats.experience}</div>
              <div className="text-blue-100 text-sm">Local Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold">{currentRegion.stats.satisfaction}</div>
              <div className="text-blue-100 text-sm">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {/* How this area works. On a network city this is the first
                    thing in the body on purpose: the visitor must not be able
                    to read this page and think we are the inspector. */}
                {network && (
                  <div className="mb-8 rounded-lg border-l-4 border-primary bg-slate-50 p-6">
                    <h2 className="font-chivo text-xl font-bold text-slate-900 mb-2">
                      How {currentRegion.name} works
                    </h2>
                    <p className="text-slate-700 leading-relaxed">
                      {operator ? (
                        <>
                          Inspections in {currentRegion.name} are carried out by{" "}
                          <strong>{operator}</strong>, an independent local inspector in our
                          network. Send us the property details and we will pass them straight
                          across. They quote you directly and carry out the work.
                        </>
                      ) : (
                        <>
                          <strong>We do not inspect in {currentRegion.name} ourselves.</strong>{" "}
                          Our own team works in Darwin and the Top End. Tell us about the
                          property and we will pass your enquiry to an independent local
                          inspector who does work here. It costs you nothing, and they quote
                          you directly — the price, the scope and the report are theirs, not
                          ours.
                        </>
                      )}
                    </p>
                  </div>
                )}

                {/* Introduction */}
                <h2 className="font-chivo text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
                  {network
                    ? `Buying or selling in ${currentRegion.name}?`
                    : `Property Inspections in ${currentRegion.name}`}
                </h2>
                <p className="text-lg text-slate-600 mb-8">
                  {currentRegion.description}
                </p>

                {/* Local Knowledge Section */}
                <div className="bg-slate-50 p-8 rounded-lg border border-slate-200 mb-10">
                  <h3 className="font-chivo text-xl font-bold text-slate-900 mb-4">
                    Local Climate Considerations
                  </h3>
                  <p className="text-slate-600 mb-6">
                    {currentRegion.localInfo.climate}
                  </p>

                  <h3 className="font-chivo text-xl font-bold text-slate-900 mb-4">
                    Housing in {currentRegion.name}
                  </h3>
                  <p className="text-slate-600">
                    {currentRegion.localInfo.housingStock}
                  </p>
                </div>

                {/* Pool rules differ substantially between states and this is
                    the most useful thing on the page. Sources are rendered so
                    the claim is checkable rather than asserted. */}
                {currentRegion.poolRule && (
                  <div className="bg-white p-8 rounded-lg border border-slate-200 mb-10">
                    <h3 className="font-chivo text-xl font-bold text-slate-900 mb-4">
                      Pool rules in {currentRegion.stateName}
                    </h3>
                    <p className="text-slate-600 leading-relaxed mb-4">
                      {currentRegion.poolRule.summary}
                    </p>
                    {currentRegion.poolRule.source && (
                      <p className="text-sm text-slate-500">
                        Source:{" "}
                        <a
                          href={currentRegion.poolRule.source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {currentRegion.poolRule.source.label}
                        </a>
                        . Requirements change — confirm your own position with the relevant
                        authority.
                      </p>
                    )}
                  </div>
                )}

                {/* Common Issues */}
                <h3 className="font-chivo text-xl font-bold text-slate-900 mb-4">
                  Common Issues We Identify in {currentRegion.name}
                </h3>
                <p className="text-slate-600 mb-6">
                  Our experienced inspectors know what to look for in {currentRegion.name} properties. Here are some common issues we frequently identify:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                  {currentRegion.localInfo.commonIssues.map((issue, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{issue}</span>
                    </div>
                  ))}
                </div>

                {/* Suburbs We Service */}
                <h3 className="font-chivo text-xl font-bold text-slate-900 mb-4">
                  Suburbs We Service in {currentRegion.name}
                </h3>
                <p className="text-slate-600 mb-6">
                  We provide professional building inspection services throughout the {currentRegion.name} region, including:
                </p>
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 mb-10">
                  <div className="flex flex-wrap gap-2">
                    {currentRegion.suburbs.map((suburb, index) => (
                      <span
                        key={index}
                        className="inline-block bg-white px-3 py-1.5 rounded-full text-sm text-slate-700 border border-slate-200"
                      >
                        {suburb}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Why Choose Us */}
                <h3 className="font-chivo text-xl font-bold text-slate-900 mb-6">
                  Why Choose Aussie Inspections in {currentRegion.name}?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">Local Knowledge</h4>
                      <p className="text-slate-600 text-sm">We understand {currentRegion.name}&apos;s unique building challenges and conditions</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">72-Hour Reports</h4>
                      <p className="text-slate-600 text-sm">Fast turnaround to keep your property transaction moving</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">Fully Insured</h4>
                      <p className="text-slate-600 text-sm">Professional indemnity and public liability coverage</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">Trusted by Agents</h4>
                      <p className="text-slate-600 text-sm">{network ? `Independent local inspectors covering ${currentRegion.name}` : "Preferred inspector for real estate professionals across the NT"}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="sticky top-24 space-y-6"
              >
                {/* Book Now Card */}
                <div className="bg-primary text-white p-8 rounded-lg">
                  <h3 className="font-chivo text-2xl font-bold mb-2">
                    {network ? "Free to enquire" : "From $260 incl. GST"}
                  </h3>
                  <p className="text-blue-100 mb-2">
                    {network ? "No obligation" : "Flat-rate pricing"}
                  </p>
                  <p className="text-sm text-blue-100 mb-6">
                    {network
                      ? `We pass your enquiry to a local ${currentRegion.name} inspector, who quotes you directly.`
                      : `Professional inspections throughout ${currentRegion.name}`}
                  </p>
                  <Button
                    asChild
                    size="lg"
                    className="w-full bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase mb-4"
                    data-testid="region-book-button"
                  >
                    <Link to="/contact">Book Inspection</Link>
                  </Button>
                  <a
                    href="tel:+61413245519"
                    className="flex items-center justify-center space-x-2 text-white hover:text-blue-100 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="font-semibold">0413 24 55 19</span>
                  </a>
                </div>

                {/* Services Card */}
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <h3 className="font-chivo text-lg font-bold text-slate-900 mb-4">
                    Our Services in {currentRegion.name}
                  </h3>
                  <div className="space-y-3">
                    {services.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        className="flex items-center space-x-3 text-slate-700 hover:text-primary transition-colors group"
                      >
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        <span>{service.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Other Regions Card */}
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
                  <h3 className="font-chivo text-lg font-bold text-slate-900 mb-4">
                    Other Service Areas
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(regionsData)
                      .filter(([key]) => key !== regionId)
                      .map(([key, region]) => (
                        <Link
                          key={key}
                          to={`/service-areas/${key}`}
                          className="flex items-center space-x-3 text-slate-700 hover:text-primary transition-colors group"
                        >
                          <MapPin className="w-4 h-4" />
                          <span>{region.name}</span>
                        </Link>
                      ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-chivo text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {network
              ? `Need an inspection in ${currentRegion.name}?`
              : `Ready to Book an Inspection in ${currentRegion.name}?`}
          </h2>
          <p className="text-lg text-slate-600 mb-2">
            {network
              ? "Send us the property details and we will pass them to a local inspector"
              : "Fast, professional building inspections for agents and homeowners"}
          </p>
          <p className="text-slate-600 mb-8">
            {network
              ? "Free to enquire • No obligation • They quote you directly"
              : "72-hour report delivery • Flat-rate pricing from $260 incl. GST"}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase px-12"
            >
              <Link to="/contact">Book Online</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-primary/20 hover:border-primary hover:bg-primary/5 font-bold tracking-wide uppercase px-12"
            >
              <a href="tel:+61413245519">Call Now</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
